import { InfoOutlined } from '@mui/icons-material';
import { Box, Divider, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardCard from '/@/components/bayer/dashboard-card';
import PieChart from '/@/components/bayer/pie-chart';
import {
    useRVActiveCampaignDashboard,
    useRVRecommendationsDashboard,
    useRVVisitReportsDashboard,
} from '/@/hooks/dashboards';
import { useAdvisorRvLinks, useAncestors, useGetUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { reverse } from '/@/utils/router';
import { getColor } from '/@/views/frost-risk/frost-risk-map/gdu-forecast';
import { ProfileIcon } from '../profile-icon';

const RVActivity = ({
    userNodeId,
    value,
    colorMap,
}: {
    userNodeId: number;
    value: number;
    colorMap: Map<number, string>;
}) => {
    const navigate = useNavigate();
    const userNodeQuery = useGetUserNode(userNodeId);
    const userNode = userNodeQuery.data;
    const consultantsQuery = useAncestors(
        { node: userNode?.id, types: ['consultant'] },
        { enabled: Boolean(userNode?.id) }
    );
    const advisorRvLinksQuery = useAdvisorRvLinks();
    const advisors = useMemo(() => {
        const advisors = advisorRvLinksQuery.data?.results?.filter((link) => link.rv.id === userNode?.id) || [];
        return advisors.map((link) => link.advisor);
    }, [advisorRvLinksQuery.data, userNode?.id]);

    const team = useMemo(() => {
        return [...(consultantsQuery.data || []), ...advisors];
    }, [consultantsQuery.data, advisors]);

    const color = colorMap.get(userNodeId) ?? '#3498DB';
    return (
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Stack spacing={1}>
                <Stack spacing={1} direction='row' alignItems='center'>
                    <Box sx={{ background: color, width: 8, height: 8, borderRadius: '50%' }} />
                    <Typography
                        variant='subtitle1'
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                            navigate(
                                reverse('RVs.detail', {
                                    params: {
                                        rvId: userNodeId,
                                    },
                                })
                            );
                        }}
                    >
                        {userNode?.name}
                    </Typography>
                </Stack>
                <Stack spacing={-0.5} direction='row' alignItems='center'>
                    {team.map((userNode, index) => (
                        <ProfileIcon
                            key={userNode.id}
                            userNode={userNode}
                            type={userNode.type as 'consultant' | 'advisor'}
                            size={26}
                            sx={{
                                position: 'relative',
                                zIndex: team.length - index,
                            }}
                        />
                    ))}
                </Stack>
            </Stack>
            <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                {value}
            </Typography>
        </Stack>
    );
};

const useRecommendationsActivity = ({ userNodeId, year }: { userNodeId: number; year?: number }) => {
    const rvRecommendationsDashboardQuery = useRVRecommendationsDashboard({
        userNodeId,
        excludeTargetNode: true,
        year,
    });
    const currentData = useMemo(() => {
        return (
            rvRecommendationsDashboardQuery.data?.recommendationsByRv.map((item) => ({
                userNodeId: item.userNodeId,
                count: item.count,
            })) || []
        ).toSorted((a, b) => b.count - a.count);
    }, [rvRecommendationsDashboardQuery.data]);
    const totalCount = useMemo(() => {
        return rvRecommendationsDashboardQuery.data?.totalCount || 0;
    }, [rvRecommendationsDashboardQuery.data]);
    return { currentData, totalCount, isLoading: rvRecommendationsDashboardQuery.isLoading };
};

const useVisitReportsActivity = ({ userNodeId, campaign }: { userNodeId: number; campaign?: number }) => {
    const rvVisitReportsDashboardQuery = useRVVisitReportsDashboard({ userNodeId, excludeTargetNode: true, campaign });
    const currentData = useMemo(() => {
        return (
            rvVisitReportsDashboardQuery.data?.visitReportsByRv.map((item) => ({
                userNodeId: item.userNodeId,
                count: item.count,
            })) || []
        ).toSorted((a, b) => b.count - a.count);
    }, [rvVisitReportsDashboardQuery.data]);
    const totalCount = useMemo(() => {
        return rvVisitReportsDashboardQuery.data?.totalCount || 0;
    }, [rvVisitReportsDashboardQuery.data]);
    return { currentData, totalCount, isLoading: rvVisitReportsDashboardQuery.isLoading };
};

const useActiveCampaignsActivity = ({ userNodeId, campaign }: { userNodeId: number; campaign?: number }) => {
    const rvActiveCampaignDashboardQuery = useRVActiveCampaignDashboard({
        userNodeId,
        excludeTargetNode: true,
        campaign,
    });
    const currentData = useMemo(() => {
        return (
            rvActiveCampaignDashboardQuery.data?.activeCampaignsByRv.map((item) => ({
                userNodeId: item.userNodeId,
                count: item.count,
            })) || []
        ).toSorted((a, b) => b.count - a.count);
    }, [rvActiveCampaignDashboardQuery.data]);
    const totalCount = useMemo(() => {
        return rvActiveCampaignDashboardQuery.data?.totalCount || 0;
    }, [rvActiveCampaignDashboardQuery.data]);
    return { currentData, totalCount, isLoading: rvActiveCampaignDashboardQuery.isLoading };
};

const ActivityCardSkeleton = () => {
    return (
        <Stack width='100%' spacing={2}>
            <Box width='80%' mx='auto !important'>
                <Stack position='relative' alignItems='center' justifyContent='center' sx={{ aspectRatio: 1 }}>
                    <Skeleton variant='circular' width='100%' height='100%' />
                    <Skeleton
                        variant='text'
                        width={60}
                        height={40}
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                        }}
                    />
                </Stack>
            </Box>
            <Stack spacing={3}>
                <Skeleton variant='text' width='60%' height={24} sx={{ alignSelf: 'center', wordBreak: 'break-all' }} />
                <Stack spacing={1}>
                    {['skeleton-1', 'skeleton-2', 'skeleton-3'].map((key) => (
                        <Stack key={key} direction='row' alignItems='center' justifyContent='space-between'>
                            <Stack spacing={1}>
                                <Stack spacing={1} direction='row' alignItems='center'>
                                    <Skeleton variant='circular' width={8} height={8} />
                                    <Skeleton variant='text' width={120} height={20} />
                                </Stack>
                                <Stack spacing={-0.5} direction='row' alignItems='center'>
                                    <Skeleton variant='circular' width={26} height={26} />
                                </Stack>
                            </Stack>
                            <Skeleton variant='text' width={30} height={20} />
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        </Stack>
    );
};

const ActivityCard = ({
    title,
    titleTooltip,
    currentData,
    totalCount,
    tooltipLabel,
    colorMap,
    isLoading,
}: {
    title: string;
    titleTooltip?: string;
    currentData: { userNodeId: number; count: number }[];
    totalCount: number;
    tooltipLabel: string;
    colorMap: Map<number, string>;
    isLoading: boolean;
}) => {
    if (isLoading) {
        return <ActivityCardSkeleton />;
    }

    return (
        <Stack width='100%' spacing={2}>
            <Box width='80%' mx='auto !important'>
                <PieChart
                    data={currentData}
                    tooltipLabel={tooltipLabel}
                    totalCount={totalCount}
                    showLegend={false}
                    colorMap={colorMap}
                />
            </Box>
            <Stack spacing={3}>
                <Stack spacing={1} direction='row' alignItems='center' justifyContent='center'>
                    <Typography variant='subtitle1' fontWeight='bold' align='center' sx={{ wordBreak: 'break-all' }}>
                        {title}
                    </Typography>
                    {titleTooltip && (
                        <Tooltip title={titleTooltip}>
                            <InfoOutlined />
                        </Tooltip>
                    )}
                </Stack>
                <Stack spacing={1}>
                    {currentData.map((item) => (
                        <RVActivity
                            key={item.userNodeId}
                            userNodeId={item.userNodeId}
                            value={item.count}
                            colorMap={colorMap}
                        />
                    ))}
                </Stack>
            </Stack>
        </Stack>
    );
};

const SquadActivitySection = ({ userNodeId, year }: { userNodeId: number; year: number }) => {
    const recommendationsData = useRecommendationsActivity({ userNodeId, year });
    const visitReportsData = useVisitReportsActivity({ userNodeId, campaign: year });
    const activeCampaignsData = useActiveCampaignsActivity({ userNodeId, campaign: year });

    // Create a color mapping for all unique userNodeIds
    const colorMap = useMemo(() => {
        const allUserNodeIds = new Set<number>();
        for (const item of recommendationsData.currentData) {
            allUserNodeIds.add(item.userNodeId);
        }
        for (const item of visitReportsData.currentData) {
            allUserNodeIds.add(item.userNodeId);
        }
        for (const item of activeCampaignsData.currentData) {
            allUserNodeIds.add(item.userNodeId);
        }

        const sortedUserNodeIds = Array.from(allUserNodeIds);
        const map = new Map<number, string>();
        for (let index = 0; index < sortedUserNodeIds.length; index++) {
            const id = sortedUserNodeIds[index];
            if (id !== undefined) {
                map.set(id, getColor(index) ?? '#3498DB');
            }
        }
        return map;
    }, [recommendationsData.currentData, visitReportsData.currentData, activeCampaignsData.currentData]);

    return (
        <DashboardCard title={_('Actividad')} subtitle={_('Actividad principal de los RV y su equipo de trabajo')}>
            <Stack
                direction={{
                    xs: 'column',
                    sm: 'row',
                }}
                divider={
                    <Divider
                        orientation={{
                            xs: 'horizontal',
                            sm: 'vertical',
                        }}
                    />
                }
                spacing={3}
                height='100%'
                justifyContent='space-between'
            >
                <ActivityCard
                    title={_('Recomendaciones')}
                    currentData={recommendationsData.currentData}
                    totalCount={recommendationsData.totalCount}
                    tooltipLabel={_('recomendaciones')}
                    colorMap={colorMap}
                    isLoading={recommendationsData.isLoading}
                />
                <ActivityCard
                    title={_('Informes de visita')}
                    currentData={visitReportsData.currentData}
                    totalCount={visitReportsData.totalCount}
                    tooltipLabel={_('informes de visita')}
                    colorMap={colorMap}
                    isLoading={visitReportsData.isLoading}
                />
                <ActivityCard
                    title={_('Campañas Activas')}
                    titleTooltip={_('Lotes con cultivos agregados en la campaña actual')}
                    currentData={activeCampaignsData.currentData}
                    totalCount={activeCampaignsData.totalCount}
                    tooltipLabel={_('campañas activas')}
                    colorMap={colorMap}
                    isLoading={activeCampaignsData.isLoading}
                />
            </Stack>
        </DashboardCard>
    );
};

export default SquadActivitySection;
